/**
 * Longest edge, in pixels, that a picked image is scaled down to before it is uploaded.
 *
 * The server crops and resizes every upload to 512×512 anyway, so sending a phone's
 * full-resolution photo (12MP, 8-12MB) buys no quality — it only makes the request big
 * enough to time out on a mobile connection, which is how it fails in practice.
 *
 * Sized against the *crop*, not the whole frame: the server upscales (and so blurs) any
 * cropped region narrower than its 512px output, so at 2400px a crop has to be tighter
 * than a fifth of the frame before it loses anything. A 2400px JPEG still lands around
 * 1MB — an order of magnitude below the originals that were failing.
 */
export const MAX_UPLOAD_IMAGE_DIMENSION = 2400;

/** JPEG quality for the downscaled image. */
const DOWNSCALE_QUALITY = 0.9;

/**
 * Scales an image blob down so its longest edge is at most `maxDimension`, preserving
 * aspect ratio. Images already within the bound are returned untouched.
 *
 * Falls back to the original blob if the browser cannot decode it (an iPhone HEIC picked
 * through a file input, say) — an upload that is merely large beats no upload at all.
 *
 * Call this *before* handing the image to a cropper: the crop coordinates the server
 * extracts with are in the source image's pixel space, so the image that gets cropped and
 * the image that gets uploaded have to be the same one.
 */
export async function downscaleImage(
  blob: Blob,
  maxDimension = MAX_UPLOAD_IMAGE_DIMENSION,
): Promise<Blob> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return blob;

  let bitmap: ImageBitmap;
  try {
    // `from-image` applies the EXIF rotation, so a photo taken in portrait doesn't come
    // out sideways once it is redrawn to a canvas.
    bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
  } catch {
    return blob;
  }

  try {
    const longestEdge = Math.max(bitmap.width, bitmap.height);
    if (longestEdge <= maxDimension) return blob;

    const scale = maxDimension / longestEdge;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const resized = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', DOWNSCALE_QUALITY),
    );
    return resized ?? blob;
  } catch {
    return blob;
  } finally {
    bitmap.close?.();
  }
}
